/**
 * SessionIdlePolicySection — Issue #289.
 *
 * Renders the admin session idle-timeout policy panel inside `/settings`.
 *
 * - Any authenticated user can view the current policy.
 * - Super-admin (*:*) or session_policy:write can update it.
 * - Backend is the authoritative gate; this UI only gates interactivity.
 * - Range: 5–480 minutes. Default: 30.
 *
 * 红线：
 * - 不展示 token、cookie、refresh token、access token 或任何凭据。
 * - 提交使用 typed useSessionPolicyCenter hook（@jinzhuan/api-client）。
 * - 前端不伪装成功；非 2xx 必须展示错误。
 */
import { useState, useEffect, type ReactElement } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Timer } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useSessionPolicyCenter } from '@/hooks/useSessionPolicy';
import { canWriteSessionPolicy } from '@/lib/sessionPolicyPermissions';

const SESSION_IDLE_MIN = 5;
const SESSION_IDLE_MAX = 480;

const schema = z.object({
  idleTimeoutMinutes: z
    .number({ invalid_type_error: '请输入整数分钟数' })
    .int('请输入整数')
    .min(SESSION_IDLE_MIN, `最小值 ${SESSION_IDLE_MIN} 分钟`)
    .max(SESSION_IDLE_MAX, `最大值 ${SESSION_IDLE_MAX} 分钟`),
});

type FormValues = z.infer<typeof schema>;

export interface SessionIdlePolicySectionProps {
  readonly permissions: readonly string[];
}

export default function SessionIdlePolicySection({
  permissions,
}: SessionIdlePolicySectionProps): ReactElement {
  const canWrite = canWriteSessionPolicy(permissions);
  const center = useSessionPolicyCenter();
  const [formError, setFormError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { idleTimeoutMinutes: 30 },
  });

  // Seed the form when policy data first arrives. Guard with !isDirty so that
  // user edits are never overwritten by subsequent re-renders or effect replays.
  const policyIdleMinutes = center.policy.data?.idleTimeoutMinutes;
  const { reset } = form;
  const { isDirty } = form.formState;
  useEffect(() => {
    if (policyIdleMinutes !== undefined && !isDirty) {
      reset({ idleTimeoutMinutes: policyIdleMinutes });
    }
  }, [policyIdleMinutes, reset, isDirty]);

  async function onSubmit(values: FormValues): Promise<void> {
    setFormError(null);
    setSuccessMsg(null);
    try {
      await center.updatePolicy({ idleTimeoutMinutes: values.idleTimeoutMinutes });
      setSuccessMsg(`已将空闲超时更新为 ${values.idleTimeoutMinutes} 分钟。`);
    } catch (err) {
      setFormError((err as Error).message);
    }
  }

  return (
    <section
      className="space-y-3"
      data-testid="settings-session-idle-policy-section"
      aria-label="会话空闲超时策略"
    >
      <div>
        <h3 className="text-lg font-medium">会话空闲超时策略</h3>
        <p className="text-sm text-muted-foreground">
          管理后台会话空闲超时配置。默认 30 分钟（范围：5–480 分钟）。超时后后端将拒绝刷新请求，
          前端自动退出登录。仅超级管理员可修改此配置。
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Timer className="h-4 w-4" aria-hidden />
            当前配置
          </CardTitle>
          <CardDescription>
            来自 GET /api/v1/admin/session-policy。后端在登录和刷新 token 时读取此值并写入
            refresh token 的 idle_expires_at 字段。
          </CardDescription>
        </CardHeader>
        <CardContent>
          {center.policy.loading && (
            <div className="text-sm text-muted-foreground" data-testid="session-idle-policy-loading">
              正在加载…
            </div>
          )}
          {center.policy.error && (
            <div
              className="text-sm text-destructive"
              data-testid="session-idle-policy-error"
              role="alert"
            >
              {center.policy.error}
            </div>
          )}
          {center.policy.data && (
            <dl
              className="grid gap-x-6 gap-y-2 text-sm md:grid-cols-3"
              data-testid="session-idle-policy-info"
            >
              <div>
                <dt className="text-xs uppercase text-muted-foreground">空闲超时</dt>
                <dd data-testid="session-idle-policy-minutes">
                  {center.policy.data.idleTimeoutMinutes} 分钟
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase text-muted-foreground">最近更新时间</dt>
                <dd data-testid="session-idle-policy-updated-at">
                  {center.policy.data.updatedAt
                    ? new Date(center.policy.data.updatedAt).toLocaleString()
                    : '从未修改（使用默认值）'}
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase text-muted-foreground">更新人</dt>
                <dd data-testid="session-idle-policy-updated-by">
                  {center.policy.data.updatedBy ?? '—'}
                </dd>
              </div>
            </dl>
          )}
        </CardContent>
      </Card>

      {canWrite && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">修改空闲超时</CardTitle>
            <CardDescription>
              超级管理员可修改。变更立即写入审计日志。新会话（登录 / 刷新）将使用新值；
              已有 refresh token 的 idle_expires_at 不追溯修改。
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form
              className="space-y-3 max-w-xs"
              data-testid="session-idle-policy-form"
              onSubmit={form.handleSubmit(onSubmit)}
            >
              <div>
                <Label htmlFor="idle-timeout-minutes">空闲超时（分钟）</Label>
                <Input
                  id="idle-timeout-minutes"
                  type="number"
                  min={SESSION_IDLE_MIN}
                  max={SESSION_IDLE_MAX}
                  data-testid="session-idle-policy-input"
                  {...form.register('idleTimeoutMinutes', { valueAsNumber: true })}
                />
                <p className="text-xs text-muted-foreground">
                  范围：{SESSION_IDLE_MIN}–{SESSION_IDLE_MAX} 分钟
                </p>
                {form.formState.errors.idleTimeoutMinutes && (
                  <p className="text-xs text-destructive">
                    {form.formState.errors.idleTimeoutMinutes.message}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-3">
                <Button
                  type="submit"
                  data-testid="session-idle-policy-submit"
                  disabled={form.formState.isSubmitting}
                >
                  {form.formState.isSubmitting ? '保存中…' : '保存'}
                </Button>
                {successMsg && (
                  <span
                    className="text-xs text-emerald-600"
                    data-testid="session-idle-policy-success"
                  >
                    {successMsg}
                  </span>
                )}
              </div>
              {formError && (
                <p
                  className="text-xs text-destructive"
                  role="alert"
                  data-testid="session-idle-policy-form-error"
                >
                  {formError}
                </p>
              )}
            </form>
          </CardContent>
        </Card>
      )}

      {!canWrite && (
        <Card>
          <CardContent className="py-4">
            <p className="text-sm text-muted-foreground" data-testid="session-idle-policy-readonly-note">
              当前账号无修改权限（需要 *:* 或 session_policy:write）。如需变更，请联系超级管理员。
            </p>
          </CardContent>
        </Card>
      )}
    </section>
  );
}
